@if (user.error()) {
  <!-- The daily cap is not a failure and retrying cannot clear it, so it gets its own colour and
       drops the Retry button: the only useful next steps are a user already opened today, or
       waiting until the time in the message. -->
  @if (loadRateLimited()) {
    <div class="p-6">
      <div class="rounded-lg border border-amber-300 bg-amber-50 p-6 max-w-2xl">
        <div class="flex items-center gap-3 mb-4">
          <i class="pi pi-lock text-amber-600 text-2xl"></i>
          <h1 class="font-bold text-2xl text-amber-800">Daily viewing limit reached</h1>
        </div>
        <p class="text-sm text-amber-800 mb-2 break-words">{{ loadErrorMessage() }}</p>
        <p class="text-sm text-amber-700">
          User <span class="font-mono break-all">{{ userId() }}</span> was not opened today, so it
          counts against the limit. Users you already opened today are still available.
        </p>
      </div>
    </div>
  } @else {
    <div class="p-6">
      <div class="rounded-lg border border-red-300 bg-red-50 p-6 max-w-2xl">
        <div class="flex items-center gap-3 mb-4">
          <i class="pi pi-exclamation-triangle text-red-600 text-2xl"></i>
          <h1 class="font-bold text-2xl text-red-800">Could not load user</h1>
        </div>
        <p class="text-sm text-red-700 mb-2">
          Loading user <span class="font-mono break-all">{{ userId() }}</span> failed.
        </p>
        <p class="text-sm text-red-700 mb-6 break-words">{{ loadErrorMessage() }}</p>
        <p-button label="Retry" icon="pi pi-refresh" (onClick)="reloadUser()"/>
      </div>
    </div>
  }
} @else if (user.hasValue()) {
  @let deletedUserValue = deletedUser();

  @if (deletedUserValue) {
    <div class="p-6">
      <div class="rounded-lg border border-red-300 bg-red-50 p-6 max-w-2xl">
        <div class="flex items-center gap-3 mb-4">
          <i class="pi pi-trash text-red-600 text-2xl"></i>
          <h1 class="font-bold text-2xl text-red-800">User deleted</h1>
          <p-tag severity="danger">DELETED</p-tag>
        </div>
        <p class="text-sm text-red-700 mb-6">
          This user has been deleted. Only minimal tombstone information is retained.
        </p>
        <div class="grid grid-cols-1 gap-y-4">
          <div>
            <span class="text-sm text-muted-color">User ID</span>
            <div class="flex items-center gap-2">
              <span class="font-mono text-sm break-all">{{ deletedUserValue.id }}</span>
              <homey-copy-button [value]="deletedUserValue.id"/>
            </div>
          </div>
          <div>
            <span class="text-sm text-muted-color">Email at deletion</span>
            <div class="flex items-center gap-2">
              <span class="text-sm break-all">{{ deletedUserValue.email ?? 'Unknown' }}</span>
              @if (deletedUserValue.email) {
                <homey-copy-button [value]="deletedUserValue.email"/>
              }
            </div>
          </div>
          <div>
            <span class="text-sm text-muted-color">Deleted at</span>
            <div class="text-sm">
              {{ deletedUserValue.deletedAt ? (deletedUserValue.deletedAt | date: dateTimeFormat) : 'Unknown' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  @let userValue = activeUser();

  @if (userValue) {
    @let syncTag = syncProcessingTag();

    <!-- The action rail is sticky, so its containing block has to span the whole page: this flex
         row wraps every section, not just the profile grid. -->
    <div class="p-6 flex flex-row gap-6">
      <div class="min-w-0 flex-1">
        <header class="mb-6">
          <!-- Tags sit beside the heading rather than inside it: they are status, not part of the
               user's name, and a heading that reads "Jane Doe active IGNORED Syncing" to a screen
               reader is worse than a heading followed by a labelled status row. -->
          <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 class="font-bold text-3xl min-w-0 break-words">{{ displayName() }}</h1>
            <div class="flex flex-wrap items-center gap-2" aria-label="User status">
              <p-tag [severity]="homeyStatusToTag(userValue.status)">{{ userValue.status }}</p-tag>
              @if (userValue.ignore) {
                <p-tag severity="danger">IGNORED</p-tag>
                <p-tag [severity]="ignoredReasonSeverity(userValue.ignoredReason)">
                  {{ ignoredReasonLabel(userValue.ignoredReason) }}
                </p-tag>
              }
              @if (syncTag) {
                <p-tag [severity]="syncTag.severity">{{ syncTag.label }}</p-tag>
              }
            </div>
          </div>

          <div class="mt-2 flex items-center gap-2">
            <span class="font-mono text-sm text-muted-color break-all">{{ userValue.id }}</span>
            <homey-copy-button [value]="userValue.id" label="User ID"/>
          </div>

          <p class="mt-1 text-sm text-muted-color">
            Created {{ userValue.createdAt | date: dateTimeFormat }}
            @if (userValue.lastSeen) {
              · Last seen {{ userValue.lastSeen | date: dateTimeFormat }}
            }
          </p>
        </header>

        <!-- Grouped into cards by subject. The previous single two-column grid put unrelated
             fields next to each other (birth date beside subscription type) purely because of the
             order they were written in. -->
        <div class="grid gap-4 xl:grid-cols-2">
          <section class="rounded-lg border border-slate-200 bg-white p-4" aria-labelledby="profile-heading">
            <h2 id="profile-heading" class="text-lg font-medium mb-4">Profile</h2>
            <div class="grid grid-cols-2 gap-x-6 gap-y-4">
              <homey-labeled label="First name" [value]="userValue.firstName"/>
              <homey-labeled label="Last name" [value]="userValue.lastName"/>
              <homey-labeled label="Email" [value]="userValue.email" [copy]="true"/>
              <div>
                <p class="text-muted-color font-light text-sm">Phone</p>
                <div class="flex flex-wrap items-center gap-2">
                  <p>{{ userValue.phone ?? '-' }}</p>
                  <p-tag [severity]="userValue.smsNotificationsEnabled ? 'success' : 'warn'">
                    {{ userValue.smsNotificationsEnabled ? 'SMS enabled' : 'Opted out' }}
                  </p-tag>
                </div>
              </div>
              <homey-labeled label="Birth date" [value]="userValue.birthDate"/>
              <homey-labeled label="Household size" [value]="'' + (userValue.householdSize ?? '')"/>
              <homey-labeled label="Rental assistance" [value]="userValue.rentalAssistance ? 'Yes' : 'No'"/>
              <homey-labeled label="Referring source" [value]="userValue.source"/>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4" aria-labelledby="subscription-heading">
            <h2 id="subscription-heading" class="text-lg font-medium mb-4">Subscription</h2>
            <div class="grid grid-cols-2 gap-x-6 gap-y-4">
              <homey-labeled label="Status" [value]="userValue.subscription?.status"
                             [tagFunction]="subscriptionStatusToTag"/>
              <homey-labeled label="App access" [value]="userValue.subscription?.paymentStatus"
                             [tagFunction]="paymentStatusToTag"/>
              <homey-labeled label="Type" [value]="userValue.subscription?.type"/>
              <homey-labeled label="Trialing" [value]="userValue.trialing ? 'Yes' : 'No'"/>
              <homey-labeled label="Next payment date"
                             [value]="userValue.subscription?.nextPaymentDate | date: dateFormat"/>
            </div>
          </section>

          <!-- The stored household total, which is what eligibility checks against. The members'
               own incomes are this split over the adults, so the total is the number to look at. -->
          <section class="rounded-lg border border-slate-200 bg-white p-4 xl:col-span-2"
                   aria-labelledby="household-income-heading">
            <h2 id="household-income-heading" class="text-lg font-medium mb-4">Household income</h2>
            @if (userValue.householdIncome; as income) {
              <p class="text-muted-color font-light text-sm">Total per year</p>
              <p class="text-lg font-semibold">{{ income.total | currency: 'USD' : 'symbol' : '1.0-0' }}</p>
              <div class="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">
                @for (item of income.breakdown; track item.type) {
                  <div>
                    <p class="text-muted-color font-light text-sm">{{ incomeTypeLabels[item.type] }}</p>
                    <p [class.text-muted-color]="item.amount === 0">
                      {{ item.amount | currency: 'USD' : 'symbol' : '1.0-0' }}
                    </p>
                  </div>
                }
              </div>
            } @else {
              <p class="text-sm text-muted-color">No household income entered yet.</p>
            }
          </section>

          <!-- Widest card, so it spans both columns: fifteen fields in a half-width card wrap into
               an unreadable ladder. -->
          <section class="rounded-lg border border-slate-200 bg-white p-4 xl:col-span-2"
                   aria-labelledby="housing-connect-heading">
            <h2 id="housing-connect-heading" class="text-lg font-medium mb-4">Housing Connect</h2>

            @if (userValue.housingConnectProfile; as hc) {
              <!-- The "HC " prefix every one of these labels used to carry is what the section
                   heading says, so it is dropped here. Sub-groups replace it as the thing that
                   tells you which part of the sync you are looking at. -->
              <div class="flex flex-col gap-5">
                <div>
                  <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-color mb-2">Status</h3>
                  <div class="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                    <homey-labeled label="Profile status" [value]="hc.status" [tagFunction]="nycProfileStatusToTag"/>
                    <homey-labeled label="Login status" [value]="hc.loginStatus" [tagFunction]="loginStatusToTag"/>
                    <homey-labeled label="Last sync status" [value]="hc.lastSyncStatus"
                                   [tagFunction]="lastSyncStatusToTag"/>
                    <homey-labeled label="Login failures" [value]="'' + hc.loginFailedCount"
                                   [tagFunction]="failureCountToTag"/>
                    <homey-labeled label="Sync failures" [value]="'' + hc.syncFailedCount"
                                   [tagFunction]="failureCountToTag"/>
                  </div>
                </div>

                <div>
                  <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-color mb-2">Last sync</h3>
                  <div class="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                    <homey-labeled label="Synced at" [value]="hc.lastSyncedAt | date: dateTimeFormat"/>
                    <homey-labeled label="Considered at"
                                   [value]="hc.lastSyncConsideredAt | date: dateTimeFormat"/>
                    <!-- Full width: sync errors are sentences with urls in them, and a third of a
                         row truncated them into uselessness. -->
                    <homey-labeled class="sm:col-span-2 lg:col-span-3" label="Error" [value]="hc.lastSyncError"/>
                  </div>
                </div>

                <div>
                  <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-color mb-2">Credentials</h3>
                  <div class="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                    <!-- The username and password are fetched only when asked for, and that
                         request is audited. Everything else here loads with the page. -->
                    @if (revealedCredentials(); as credentials) {
                      <homey-labeled label="Username" [value]="credentials.username" [copy]="true"/>
                      <homey-labeled label="Password" [value]="credentials.password" [copy]="true"/>
                    } @else {
                      <div class="sm:col-span-2">
                        <p class="text-muted-color font-light text-sm">Username and password</p>
                        <div class="flex items-start gap-2">
                          <p>Hidden</p>
                          <homey-submit-button label="Reveal" severity="secondary" size="small"
                                               [submitFunction]="revealCredentials"/>
                        </div>
                        @if (credentialsError(); as error) {
                          <p class="mt-2 text-sm text-red-600">{{ error }}</p>
                        }
                      </div>
                    }
                    <homey-labeled label="Email 1" [value]="hc.email1" [copy]="true"/>
                    <homey-labeled label="Email 2" [value]="hc.email2" [copy]="true"/>
                  </div>
                  @if (revealedCredentials()) {
                    <p-button class="mt-4 inline-block" label="Hide credentials" severity="secondary"
                              size="small" icon="pi pi-eye-slash" (onClick)="hideCredentials()"/>
                  }
                </div>

                <div>
                  <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-color mb-2">Flags</h3>
                  <div class="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                    <homey-labeled label="Ignored" [value]="hc.ignore ? 'Yes' : 'No'"/>
                    <homey-labeled label="Ignored reason" [value]="hc.ignoredReason"/>
                    <homey-labeled label="Suppress unknown emails" [value]="hc.suppressUnknownEmails ? 'Yes' : 'No'"/>
                  </div>
                </div>
              </div>
            } @else {
              <!-- Beats fifteen fields all reading "-", which looks like a load failure. -->
              <p class="text-sm text-muted-color">No Housing Connect profile for this user yet.</p>
            }
          </section>
        </div>

        <section class="mt-8" aria-labelledby="connected-accounts-heading">
          <h2 id="connected-accounts-heading" class="text-lg font-medium mb-3">
            Connected accounts
            <span class="text-muted-color font-normal">({{ userValue.connectedAccounts.length }})</span>
          </h2>
          <div class="rounded-lg border border-slate-200 bg-white p-4">
            @if (userValue.connectedAccounts.length) {
              <div class="flex flex-col gap-3">
                @for (account of userValue.connectedAccounts; track account.authId) {
                  <div class="flex flex-wrap items-center justify-between gap-4">
                    <div class="flex flex-col gap-1 min-w-0">
                      <span class="font-mono text-sm text-surface-700 break-all">{{ account.authId }}</span>
                      <span class="text-sm text-muted-color">{{ account.username ?? 'No username stored' }}</span>
                    </div>
                    <p-tag [severity]="connectedAccountSeverity(account.provider)">
                      {{ connectedAccountProviderLabel(account.provider) }}
                    </p-tag>
                  </div>
                }
              </div>
            } @else {
              <p class="text-sm text-muted-color">No connected accounts recorded.</p>
            }
          </div>
        </section>

        <!-- Counts in the headings: these tables scroll inside a fixed height, so the number of
             rows is otherwise invisible without dragging the scrollbar. -->
        <section class="mt-10" aria-labelledby="emails-heading">
          <h2 id="emails-heading" class="text-lg font-medium mb-3">
            Emails
            <span class="text-muted-color font-normal">
              ({{ userValue.mails.length }}{{ userValue.mailsTruncated ? '+' : '' }})
            </span>
            @if (userValue.mailsTruncated) {
              <span class="text-sm text-muted-color font-normal">newest {{ userValue.mails.length }} shown</span>
            }
          </h2>
          <p-table
            size="small"
            [value]="userValue.mails"
            [scrollable]="true"
            [scrollHeight]="tableScrollHeight"
            [tableStyle]="{'min-width': '60rem'}"
          >
            <ng-template pTemplate="header">
              <tr class="[&>th]:whitespace-nowrap">
                <th>Status</th>
                <th>Timestamp</th>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Error message</th>
                <th><span class="sr-only">Download</span></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-email [homeyTypedTableRow]="userValue.mails">
              <tr class="align-top">
                <td>
                  <p-tag [severity]="email.status === 'processed' ? 'success' : email.status === 'not-recognised' ? 'warn' : 'danger'">
                    {{ email.status }}
                  </p-tag>
                </td>
                <td class="whitespace-nowrap">{{ email.timestamp | date: dateTimeFormat }}</td>
                <td class="max-w-56 truncate" [title]="email.recipient">{{ email.recipient }}</td>
                <td class="max-w-80 truncate" [title]="email.subject">{{ email.subject }}</td>
                <td class="text-xs">
                  @if (email.status !== 'processed') {
                    <span class="line-clamp-2 max-w-72 break-words" [title]="email.errorMessage">
                      {{ email.errorMessage }}
                    </span>
                  }
                </td>
                <!-- Audited and rate-limited server-side, which is why it is a deliberate click
                     per email rather than a link rendered with the row. -->
                <td class="text-right">
                  <p-button
                    icon="pi pi-download"
                    label="Download"
                    severity="secondary"
                    size="small"
                    [loading]="downloadingEmailId() === email.id"
                    (onClick)="downloadEmail(email.id)"
                  />
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6" class="text-center">No emails found</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="mt-10" aria-labelledby="applications-heading">
          <h2 id="applications-heading" class="text-lg font-medium mb-3">
            Applications <span class="text-muted-color font-normal">({{ userValue.applications.length }})</span>
          </h2>
          <p-table [value]="userValue.applications" [scrollable]="true" [scrollHeight]="tableScrollHeight">
            <ng-template pTemplate="header">
              <tr>
                <th>Status</th>
                <th>Apartment Id</th>
                <th>Created At</th>
                <th>Method</th>
                <th>Log Nr</th>
                <th>NYC Status</th>
                <th>Withdrawn</th>
                <th>Failure Reason</th>
                <th>Id</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-application [homeyTypedTableRow]="userValue.applications">
              <tr>
                <td>
                  <p-tag [severity]="applicationStatusToTag(application.status)">{{ application.status }}</p-tag>
                </td>
                <td>{{ application.lotteryId }}</td>
                <td class="whitespace-nowrap">{{ application.createdAt | date: dateTimeFormat }}</td>
                <td>{{ application.method }}</td>
                <td>{{ application.logNr }}</td>
                <td>{{ application.nycStatusLabel }}</td>
                <td>{{ application.withdrawnManually ? 'Yes' : '-' }}</td>
                <!-- Failure reasons are full sentences (NYC api errors carry a url), so this cell
                     wraps and is capped instead of stretching the table sideways. -->
                <td class="max-w-md whitespace-normal break-words">{{ application.failureReason }}</td>
                <td class="font-mono text-xs text-muted-color break-all">{{ application.id }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="9" class="text-center">No applications found</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="mt-10" aria-labelledby="domain-events-heading">
          <h2 id="domain-events-heading" class="text-lg font-medium mb-3">
            Domain events
            <span class="text-muted-color font-normal">
              ({{ userValue.domainEvents.length }}{{ userValue.domainEventsTruncated ? '+' : '' }})
            </span>
            @if (userValue.domainEventsTruncated) {
              <span class="text-sm text-muted-color font-normal">newest {{ userValue.domainEvents.length }} shown</span>
            }
          </h2>
          <p-table [value]="userValue.domainEvents" [scrollable]="true" [scrollHeight]="tableScrollHeight">
            <ng-template pTemplate="header">
              <tr>
                <th>Type</th>
                <th>Timestamp</th>
                <th>Payload</th>
                <th>ID</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-event [homeyTypedTableRow]="userValue.domainEvents">
              <tr>
                <td class="font-medium">{{ event.eventType }}</td>
                <td class="whitespace-nowrap">{{ event.timestamp | date: dateTimeFormat }}</td>
                <td>
                  <pre class="text-xs whitespace-pre-wrap break-words">{{ event.payload | json }}</pre>
                </td>
                <td class="font-mono text-xs text-muted-color break-all">{{ event.id }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4" class="text-center">No domain events found</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      </div>

      <!-- tabindex allows focus to land back on the rail when the dialog's opener is disabled by
           the very action that was just confirmed. -->
      <aside
        #actionRail
        tabindex="-1"
        aria-label="Actions"
        class="w-60 sticky top-6 self-start rounded-lg border border-slate-200 bg-white p-4 outline-none">
        <!-- A native `<button disabled>` dispatches no pointer events, so each tooltip has to live
             on a wrapper and the button has to let pointer events fall through to it. -->
        <h2 class="text-xs font-semibold uppercase tracking-wide text-muted-color mb-2">Housing Connect</h2>
        <div class="flex flex-col gap-2">
          <span
            #registerInNycTrigger
            class="flex flex-col"
            [pTooltip]="registerInNycTooltip()"
            tooltipPosition="left"
            [tooltipDisabled]="!registerInNycDisabled()">
            <p-button
              label="Register in NYC"
              icon="pi pi-user-plus"
              severity="secondary"
              [outlined]="true"
              styleClass="w-full !justify-start"
              [disabled]="registerInNycDisabled()"
              [style.pointer-events]="registerInNycDisabled() ? 'none' : null"
              (onClick)="registerInNyc()"/>
          </span>
          <span
            #reRegisterTrigger
            class="flex flex-col"
            [pTooltip]="reRegisterTooltip()"
            tooltipPosition="left"
            [tooltipDisabled]="!reRegisterDisabled()">
            <p-button
              label="Re-register"
              icon="pi pi-refresh"
              severity="secondary"
              [outlined]="true"
              styleClass="w-full !justify-start"
              [disabled]="reRegisterDisabled()"
              [style.pointer-events]="reRegisterDisabled() ? 'none' : null"
              (onClick)="openReRegister()"/>
          </span>
        </div>

        <h2 class="text-xs font-semibold uppercase tracking-wide text-muted-color mt-5 mb-2">Account</h2>
        <div class="flex flex-col gap-2">
          <span
            class="flex flex-col"
            [pTooltip]="sendTextMessageTooltip()"
            tooltipPosition="left"
            [tooltipDisabled]="!sendTextMessageDisabled()">
            <p-button
              label="Send text message"
              icon="pi pi-comment"
              severity="secondary"
              [outlined]="true"
              styleClass="w-full !justify-start"
              [disabled]="sendTextMessageDisabled()"
              [style.pointer-events]="sendTextMessageDisabled() ? 'none' : null"
              (onClick)="goToSendTextMessage()"/>
          </span>
          <span
            #markAsPaidTrigger
            class="flex flex-col"
            pTooltip="This user already has paid access."
            tooltipPosition="left"
            [tooltipDisabled]="!markAsPaidDisabled()">
            <p-button
              label="Mark as paid"
              icon="pi pi-dollar"
              severity="secondary"
              [outlined]="true"
              styleClass="w-full !justify-start"
              [disabled]="markAsPaidDisabled()"
              [style.pointer-events]="markAsPaidDisabled() ? 'none' : null"
              (onClick)="openMarkAsPaid()"/>
          </span>
        </div>

        <div class="border-t border-slate-200 mt-5 pt-4">
          <p-button
            label="Delete user"
            icon="pi pi-trash"
            severity="danger"
            [outlined]="true"
            styleClass="w-full !justify-start"
            (onClick)="confirmDelete()"/>
        </div>
      </aside>
    </div>

    <p-dialog
      header="Register in NYC"
      [modal]="true"
      [visible]="nycAuth() !== undefined"
      (visibleChange)="closeRegisterInNycDialog()"
      (onHide)="restoreRegisterInNycFocus()"
      [breakpoints]="{ '960px': '85vw' }"
      [style]="{ width: '50rem' }">
      @if (nycAuth(); as nycAuthValue) {
        <div class="flex flex-row justify-between items-center mb-6">
          <span class="block">NYC Profile Information</span>
        </div>
        <div class="flex items-center gap-4 mb-4">
          <div class="font-semibold w-24">Email 1</div>
          <div>{{ nycAuthValue.email1 }}</div>
        </div>
        <div class="flex items-center gap-4 mb-4">
          <div class="font-semibold w-24">Email 2</div>
          <div>{{ nycAuthValue.email2 }}</div>
        </div>
        <!-- The dialog no longer carries the credentials in with it: registering in NYC by hand
             needs them, but opening the dialog to mark someone as registered does not, so they
             stay behind the same audited reveal as the panel. -->
        @if (revealedCredentials(); as credentials) {
          <div class="flex items-center gap-4 mb-4">
            <div class="font-semibold w-24">Username</div>
            <div>{{ credentials.username }}</div>
          </div>
          <div class="flex items-center gap-4 mb-8">
            <div class="font-semibold w-24">Password</div>
            <div>{{ credentials.password }}</div>
          </div>
        } @else {
          <div class="mb-8">
            <div class="flex items-center gap-4">
              <div class="font-semibold w-24">Credentials</div>
              <homey-submit-button label="Reveal" severity="secondary" size="small"
                                   [submitFunction]="revealCredentials"/>
            </div>
            @if (credentialsError(); as error) {
              <p class="mt-2 text-sm text-red-600">{{ error }}</p>
            }
          </div>
        }
        <div class="flex justify-end gap-2">
          <p-button label="Cancel" severity="secondary" (onClick)="closeRegisterInNycDialog()"/>
          <homey-submit-button label="Mark as registered" [submitFunction]="confirmRegisteredInNyc"/>
        </div>
      }
    </p-dialog>

    <p-dialog
      header="Mark as paid"
      [modal]="true"
      [visible]="markAsPaidVisible()"
      (visibleChange)="closeMarkAsPaidDialog()"
      (onHide)="restoreMarkAsPaidFocus()"
      [breakpoints]="{ '960px': '85vw' }"
      [style]="{ width: '32rem' }">
      <p class="mb-4">
        Gives this user an active monthly subscription with no payment behind it and no end date.
        Pick the provider it should be recorded under.
      </p>
      <p-selectbutton
        class="mb-8"
        [options]="markAsPaidTypeOptions"
        optionLabel="label"
        optionValue="value"
        [allowEmpty]="false"
        [ngModel]="markAsPaidType()"
        (ngModelChange)="markAsPaidType.set($event)"
        ariaLabel="Subscription provider"/>
      <div class="flex justify-end gap-2">
        <p-button label="Cancel" severity="secondary" (onClick)="closeMarkAsPaidDialog()"/>
        <homey-submit-button label="Mark as paid" [submitFunction]="confirmMarkAsPaid"/>
      </div>
    </p-dialog>

    <p-dialog
      header="Re-register in NYC"
      [modal]="true"
      [visible]="reRegisterVisible()"
      (visibleChange)="closeReRegisterDialog()"
      (onHide)="restoreReRegisterFocus()"
      [breakpoints]="{ '960px': '85vw' }"
      [style]="{ width: '40rem' }">
      <p class="mb-4 text-sm text-muted-color">
        Sends this profile back to the start of registration under the addresses below and queues a
        fresh attempt straight away. The stored password is kept. Both addresses also decide which
        user an inbound NYC email belongs to, so changing one re-points that routing. Both must be
        Homey addresses, or NYC's verification mail never arrives.
      </p>

      @if (reRegisterNeedsNewIdentity()) {
        <p class="mb-6 text-sm text-amber-600">
          NYC already has an account under this user's current username and first email address, so
          this attempt needs a new username and a different email 1 — re-using either one would only
          be rejected as a duplicate.
        </p>
      }

      <div class="flex flex-col gap-4 mb-8">
        <label class="flex flex-col gap-1">
          <span class="font-semibold text-sm">NYC email 1</span>
          <input
            pInputText
            type="email"
            [ngModel]="reRegisterEmail1()"
            (ngModelChange)="reRegisterEmail1.set($event)"
            aria-label="NYC email 1"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="font-semibold text-sm">NYC email 2</span>
          <input
            pInputText
            type="email"
            [ngModel]="reRegisterEmail2()"
            (ngModelChange)="reRegisterEmail2.set($event)"
            aria-label="NYC email 2"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="font-semibold text-sm">Username</span>
          <input
            pInputText
            [ngModel]="reRegisterUsername()"
            (ngModelChange)="reRegisterUsername.set($event)"
            [placeholder]="reRegisterNeedsNewIdentity() ? 'Required - must differ from the current one' : 'Leave blank to keep the current username'"
            aria-label="Housing Connect username"
          />
          <!-- Not prefilled: the current username only reaches this app through the audited reveal
               on the page behind this dialog, which is capped at ten a day. -->
          <span class="text-xs text-muted-color">
            Use Reveal on the page behind this dialog to see the current one.
          </span>
        </label>
      </div>

      @if (reRegisterError(); as error) {
        <p class="mb-4 text-sm text-red-600">{{ error }}</p>
      }

      <div class="flex justify-end gap-2">
        <p-button label="Cancel" severity="secondary" (onClick)="closeReRegisterDialog()"/>
        <homey-submit-button
          label="Re-register"
          severity="warn"
          [submitFunction]="submitReRegister"
          [disabled]="!reRegisterEmail1().trim() || !reRegisterEmail2().trim()"
        />
      </div>
    </p-dialog>
  }
} @else {
  <div class="p-6 flex justify-center">
    <p-progress-spinner ariaLabel="Loading user"/>
  </div>
}
